import { Grid, FormHelperText, IconButton, Box, Button, Alert, CircularProgress } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import CustomInputField from '../../../ui/primitives/CustomInputField';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useState } from 'react';

const initialCustomerState = {
  firstName: '',
  lastName: '',
  email: '',
  organizationName: '',
  password: '',
  submit: null,
};

const BaseCustomerSchema = Yup.object().shape({
  firstName: Yup.string().max(255).required('First Name is required'),
  lastName: Yup.string().max(255),
  email: Yup.string()
    .email('Must be a valid email')
    .max(255)
    .required('Email is required'),
  organizationName: Yup.string()
    .max(255)
    .required('Organization Name is required'),
});

const InviteSchema = BaseCustomerSchema;

const CreateSchema = BaseCustomerSchema.shape({
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
});

const EditSchema = BaseCustomerSchema;

const CustomerBaseFormFields = ({
  values,
  touched,
  errors,
  setFieldValue,
  isEdit = false,
}) => (
  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <CustomInputField
        label="First Name"
        id="firstName"
        value={values.firstName}
        required
        placeholder="Enter first name"
        error={touched.firstName && errors.firstName}
        onChange={(id, value) => setFieldValue(id, value)}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <CustomInputField
        label="Last Name"
        id="lastName"
        value={values.lastName}
        placeholder="Enter last name"
        error={touched.lastName && errors.lastName}
        onChange={(id, value) => setFieldValue(id, value)}
      />
    </Grid>
    <Grid item xs={12}>
      <CustomInputField
        label="Email Address"
        id="email"
        value={values.email}
        required
        placeholder="Enter email address"
        error={touched.email && errors.email}
        onChange={(id, value) => setFieldValue(id, value)}
        disabled={isEdit}
      />
    </Grid>
    <Grid item xs={12}>
      <CustomInputField
        label="Name"
        id="organizationName"
        value={values.organizationName}
        required
        placeholder="Enter organization name"
        error={touched.organizationName && errors.organizationName}
        onChange={(id, value) => setFieldValue(id, value)}
      />
    </Grid>
  </Grid>
);

export const InviteCustomerForm = ({ handleInviteSubmit, loading }) => {
  return (
    <Formik
      initialValues={initialCustomerState}
      validationSchema={InviteSchema}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await handleInviteSubmit(values);
          setStatus({ success: true });
          setSubmitting(false);
        } catch (err) {
          setStatus({ success: false });
          setErrors({ submit: err.message });
          setSubmitting(false);
        }
      }}>
      {({ errors, touched, values, setFieldValue, handleSubmit, isSubmitting }) => (
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ p: 1 }}>
          <CustomerBaseFormFields
            values={values}
            touched={touched}
            errors={errors}
            setFieldValue={setFieldValue}
          />
          {errors.submit && (
            <FormHelperText error sx={{ mt: 2 }}>
              {errors.submit}
            </FormHelperText>
          )}
          <Button
            variant="contained"
            type="submit"
            fullWidth
            disabled={isSubmitting || loading}
            startIcon={
              isSubmitting || loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{ mt: 3 }}>
            Send Invitation
          </Button>
        </Box>
      )}
    </Formik>
  );
};

export const CreateCustomerForm = ({ handleCreateDirectlySubmit, loading }) => {
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
  return (
    <Formik
      initialValues={initialCustomerState}
      validationSchema={CreateSchema}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await handleCreateDirectlySubmit(values);
          setStatus({ success: true });
          setSubmitting(false);
        } catch (err) {
          setStatus({ success: false });
          setErrors({ submit: err.message });
          setSubmitting(false);
        }
      }}>
      {({ errors, touched, values, setFieldValue, handleSubmit, isSubmitting }) => (
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ p: 1 }}>
          <CustomerBaseFormFields
            values={values}
            touched={touched}
            errors={errors}
            setFieldValue={setFieldValue}
          />

          <Box sx={{ mt: 2 }}>
            <CustomInputField
              label="Password"
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={values.password}
              error={touched.password && errors.password}
              onChange={(id, value) => setFieldValue(id, value)}
              placeholder="********"
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={togglePasswordVisibility}
                    edge="end"
                    title={showPassword ? 'Hide' : 'Show'}
                    size="small">
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                ),
              }}
            />
          </Box>

          {errors.submit && (
            <FormHelperText error sx={{ mt: 2 }}>
              {errors.submit}
            </FormHelperText>
          )}

          <Button
            variant="contained"
            type="submit"
            fullWidth
            disabled={isSubmitting || loading}
            startIcon={
              isSubmitting || loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{ mt: 3 }}>
            Create Account
          </Button>
        </Box>
      )}
    </Formik>
  );
};

export const EditCustomerForm = ({ customer, handleEditSubmit, loading }) => {
  const initialData = {
    ...initialCustomerState,
    ...customer,
    submit: null,
  };
  const safeInitialData = Object.fromEntries(
    Object.entries(initialData).map(([key, value]) => [
      key,
      value === null || value === undefined ? '' : value,
    ])
  );

  return (
    <Formik
      initialValues={safeInitialData}
      validationSchema={EditSchema}
      enableReinitialize={true}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await handleEditSubmit(values);
          setStatus({ success: true });
          setSubmitting(false);
        } catch (err) {
          setStatus({ success: false });
          setErrors({ submit: err.message });
          setSubmitting(false);
        }
      }}>
      {({ errors, touched, values, setFieldValue, handleSubmit, isSubmitting }) => (
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ p: 1 }}>
          <CustomerBaseFormFields
            values={values}
            touched={touched}
            errors={errors}
            setFieldValue={setFieldValue}
            isEdit={true}
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            Note: Updating the customer details here will not change their password.
          </Alert>

          {errors.submit && (
            <FormHelperText error sx={{ mt: 2 }}>
              {errors.submit}
            </FormHelperText>
          )}

          <Button
            variant="contained"
            color="success"
            type="submit"
            fullWidth
            disabled={isSubmitting || loading}
            startIcon={
              isSubmitting || loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{ mt: 3 }}>
            Save Changes
          </Button>
        </Box>
      )}
    </Formik>
  );
};